// composables/useReaderConversation.js
// Phase 2: Extracted from utils/reader-conversation.js
// All state is local to each composable instance — no module-level bleed.
import { reactive, ref } from 'vue'
import axios from 'axios'
import { static_store, store } from '@/store'
import { pluginRegistry } from '@/plugins'
import { load_card_reading_session } from '@/utils/reading-session'
import { trackOnce, isReadingViewed } from '@/utils/analytics'
// Shared app-wide, not per instance: see the module.
import { groundingArriving } from '@/utils/grounding-arriving'
import { keepReadingBar } from '@/utils/reading-bar'
import { PACE } from '@/utils/reveal-pacing'
import { lessonWelcomeOf, isLessonCard, isLateReply } from '@/utils/lesson-draw'

// Whole lines streamed in one frame that open a bubble of their own (recipient journey Part 4).
const WHOLE_LINES = new Set(['gift-opener', 'deeper-carry', 'deeper-ask',
  // Talk it through: each line of the conversation after a reading is whole.
  'talk-question', 'talk-reply', 'talk-card', 'talk-close',
  // The Mystic's line before a pull the seeker asked for from the menu.
  'talk-pull',
  // The gut line before the draw (journal practice 2026-10-28): the ask and its acknowledgement.
  'gut-ask', 'gut-ack'])

export function useReaderConversation() {
  // ─── Local State (fresh per instance) ─────────────────────────── //
  const ws = ref(null)
  const lock_interface = ref(false)
  const disable_insight_help = ref(false)
  const ReadingSessionID = ref('')
  const loadingHeadsUpMessage = ref('')

  const readingTitle = ref('')
  const readingPast = ref('')
  const readingPresent = ref('')
  const readingFuture = ref('')
  const readingAdvice = ref('')
  // Generic per-position description map keyed by the BE-streamed
  // reference name (derived from spread.positions[i].label). Adding
  // a new spread no longer requires a new top-level ref + switch
  // case — the new role's text flows into this map automatically.
  // Existing per-role refs (readingPast/Present/Future/Advice) stay
  // for FourCardReading.vue compatibility and are mirrored into the
  // map by the same handler, so legacy + new components both see
  // the same value during the migration.
  const readingPositions = ref({})
  const readingAnalysis = ref('')
  const readingIllustrationTitle = ref('')
  const readingImage = ref('')
  const ReadingSummaryTitle = ref('')
  const readingIsReady = ref(false)
  // The reading reached the seeker. Once per reading: a resumed or reloaded
  // session announces it again.
  function trackReadingComplete() {
    const id = ReadingSessionID.value || store.latest_reading_session
    if (!id || isReadingViewed(id)) return
    trackOnce(`reading_complete:${id}`, 'reading_complete', {
      item_id: id, reader: store.current_reader_subdomain,
    })
  }

  const astrologyTeaser = ref('')
  const astrologySummary = ref('')
  const astrologySummaryTitle = ref('')
  const astrologySharedTitle = ref('')
  const astrologyImageCaption = ref('')
  const astrologyImage = ref('')
  const astrologyTwoHourLimit = ref(false)

  const sharedReadingTitle = ref('')
  const sharedReadingImage = ref('https://via.placeholder.com/400x200?text=Reading+Overview')
  const sharedReadingPast = ref('')
  const sharedReadingPresent = ref('')
  const sharedReadingFuture = ref('')
  const sharedReadingAdvice = ref('')
  const sharedReadingSummaryTitle = ref('')
  const sharedReadingAnalysis = ref('')
  const sharedIllustrationTitle = ref('')

  const ChatInstructions = ref('')
  const LeftActionButton = ref([])
  const RightActionButton = ref([])
  const StaticSuggestions = ref('')
  const selecting_cards = ref(false)
  // Mid-draw resume payload ({card_count, picked:[{card_id,name}], focus_message}).
  // ReaderPage watches this to re-arm the card grid seeded with the cards the
  // seeker already pulled, so Continue resumes the SAME draw instead of resetting.
  const resumeCardSelection = ref(null)
  // A lesson's full reading names its slots Before · Now · Ahead · What helps
  // (lesson-redo DESIGN.md E2.7): for that reading only, so a reset forgets them.
  const lessonSlotLabelsLive = ref(null)
  // "Get the full reading" (API.md 6.4, v1.4): the conversation reply's
  // { lesson_full, quick_actions }, for the page (paid state, balance, slots,
  // or `short`: the store). A new object per reply.
  const lessonFullReply = ref(null)
  // Every reading is paid at its start (2026-10-29, no more Reveal): the
  // draw's reply { reading_charge, quick_actions }, for the page (balance, or
  // `short`: the picker called off and the store). A new object per reply.
  const readingChargeReply = ref(null)
  // A lesson's draw or talk as the last welcome told it (API.md 6.2,
  // lessonWelcomeOf); `resumed` is a reload the page restores. Null otherwise.
  const lessonWelcome = ref(null)
  // Talk it through: the Mystic's parting line when the seeker taps Finish
  // mid-conversation. Finish leaves at once, so the page shows it over home.
  const talkParting = ref('')
  const showLeftActionButton = ref(false)
  const showRightActionButton = ref(false)

  const scrollToShareTrigger = ref(false)
  const selectCardsFocusMessage = ref('')

  const currentWidget = ref(null)
  const disablePageAfterEmailActivation = ref(false)
  const awaitEmailActivationWindow = ref(false)

  // ─── Internal reactive state ──────────────────────────────────── //
  const _state = reactive({
    store,
    static_store,
    querent_subscription_expiration: '',
  })

  // ─── Private helpers ──────────────────────────────────────────── //
  function sanitizeString(input) {
    if (typeof input !== 'string') {
      return ''
    }
    return input.replace(/^"(.*)"$/, '$1').replace(/\\+/g, '')
  }

  function setWidget(type, message) {
    currentWidget.value = { type, payload: JSON.parse(message) }
    disable_insight_help.value = true
  }

  function parseActionButton(data, buttonRef, showRef, label) {
    try {
      if (!data.message || data.message === 'None') {
        buttonRef.value = []
        showRef.value = false
      } else {
        // The BE now sends valid JSON. (The old ' → " sanitiser corrupted any
        // value with an apostrophe, e.g. "I'd like…", and dropped the buttons.)
        const parsed = JSON.parse(data.message)
        buttonRef.value = Array.isArray(parsed) ? parsed : []
        showRef.value = buttonRef.value.length > 0
      }
    } catch (error) {
      console.error(`Failed to parse ${label}:`, error, data.message)
      buttonRef.value = []
      showRef.value = false
    }
  }

  // ─── Reset functions ──────────────────────────────────────────── //
  function resetAstrologyReadingState() {
    [
      astrologyTeaser,
      astrologySummary,
      astrologySummaryTitle,
      astrologySharedTitle,
      astrologyImageCaption,
      astrologyImage,
    ].forEach((refValue) => (refValue.value = ''))
  }

  function resetReadingState() {
    [
      readingTitle,
      readingPast,
      readingPresent,
      readingFuture,
      readingAdvice,
      readingAnalysis,
      readingIllustrationTitle,
      readingImage,
      ReadingSummaryTitle,
      readingIsReady,
    ].forEach((refValue) => (refValue.value = ''))
    readingPositions.value = {}
    lessonSlotLabelsLive.value = null
    groundingArriving.value = false
  }

  // ─── WebSocket ────────────────────────────────────────────────── //
  // The socket is the only way any chat spinner ever clears, so it has to
  // come back on its own. Before 2026-09-24 a dropped socket (idle, a phone
  // sleeping, the server restarting) just logged "closed": "Preparing your
  // reading…" / "Conjuring insight…" then spun forever, and the next reader
  // entry deadlocked in disconnectWebSocket() waiting for a close event a
  // dead socket never fires (the full-screen overlay never came down; only a
  // reload cleared it). Seen in production on 2026-09-23.
  let lastState = null
  let lastMessages = null
  let reconnectTimer = null
  let reconnectAttempts = 0
  let closingOnPurpose = false

  function connectWebSocket(state) {
    lastState = state
    return new Promise((resolve, reject) => {
      const socketId = Date.now()

      if (ws.value) {
        console.log(`[WS ${socketId}] Closing previous socket`)
        const old = ws.value
        ws.value = null
        old.onclose = null
        try { old.close() } catch (e) { /* already gone */ }
      }

      const apiUrl = static_store.webSocketUrl || static_store.apiUrl
      const socket_address = `${apiUrl}/ws/reader-conversation/`
      const query_string = `?socket_session_id=${state.store.session_id}`
      const socket = new WebSocket(`${socket_address}${query_string}`)
      ws.value = socket

      socket.onopen = () => {
        reconnectAttempts = 0
        resolve()
      }

      socket.onerror = (error) => {
        console.error(`[WS ${socketId}] error:`, error)
        reject(error)
      }

      socket.onclose = () => {
        console.log(`[WS ${socketId}] closed`)
        if (ws.value === socket) ws.value = null
        if (!closingOnPurpose) scheduleReconnect()
      }
    })
  }

  // Exponential backoff, capped at 30s; stops when the session is gone or a
  // deliberate disconnect happens.
  function scheduleReconnect() {
    if (reconnectTimer || !lastState || !lastState.store || !lastState.store.session_id) return
    const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts))
    reconnectAttempts += 1
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null
      try {
        await connectWebSocket(lastState)
        if (lastMessages) handleWebSocketMessages(lastMessages)
      } catch (e) {
        scheduleReconnect()
      }
    }, delay)
  }

  // Idempotently ensure the WebSocket is open. Returns when readyState
  // is OPEN.
  async function ensureWebSocketConnected(state, messages) {
    const OPEN = (typeof WebSocket !== 'undefined') ? WebSocket.OPEN : 1
    if (ws.value && ws.value.readyState === OPEN) {
      return
    }
    await connectWebSocket(state)
    if (messages) {
      handleWebSocketMessages(messages)
    }
  }

  // Always settles: a socket that is already closing/closed fires no close
  // event, and a close that never arrives is bounded by a short guard.
  function disconnectWebSocket() {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
    return new Promise((resolve) => {
      const socket = ws.value
      if (!socket) { resolve(); return }
      const CLOSING = (typeof WebSocket !== 'undefined') ? WebSocket.CLOSING : 2
      const CLOSED = (typeof WebSocket !== 'undefined') ? WebSocket.CLOSED : 3
      ws.value = null
      socket.onerror = null
      socket.onopen = null
      socket.onmessage = null
      if (socket.readyState === CLOSING || socket.readyState === CLOSED) {
        socket.onclose = null
        resolve()
        return
      }
      closingOnPurpose = true
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        closingOnPurpose = false
        resolve()
      }
      const guard = setTimeout(done, 2000)
      socket.onclose = () => { clearTimeout(guard); done() }
      try { socket.close() } catch (e) { clearTimeout(guard); done() }
    })
  }

  // ─── WebSocket Message Handler ────────────────────────────────── //
  // The closing counsel (advice, by default) streams at draw time but belongs
  // below the weave. Hold it here, release it when the summary is on screen.
  let heldSummaryMessages = null
  function revealSummaryHeldCards() {
    if (!heldSummaryMessages || !heldSummaryMessages.value) return
    heldSummaryMessages.value.forEach((m) => {
      if (m._holdForSummary) { m._holdForSummary = false; m._summaryDelivered = true }
    })
  }

  const SPOKEN_CONTEXTS = new Set(['salutation-message', 'welcome-message', 'reader-conversation', 'welcome-question'])
  function handleWebSocketMessages(messages) {
    lastMessages = messages
    heldSummaryMessages = messages
    let currentContext = null
    // Post-reading follow-up gating: the follow-up question streams as
    // welcome-message + reference=reading_is_ready. Hold it out of the chat
    // (tagged _holdForReading — the chat skips those) until the reading's
    // illustration arrives, or a fallback timer fires, so it never pops in
    // mid-generation. revealFollowUp() clears the tag to surface it.
    let heldFollowUpMsg = null
    let followUpRevealTimer = null
    const revealFollowUp = () => {
      if (followUpRevealTimer) { clearTimeout(followUpRevealTimer); followUpRevealTimer = null }
      if (heldFollowUpMsg) {
        // Mutate THROUGH the reactive array (not the raw object ref) so the
        // chat's v-for re-renders and the held bubble actually appears.
        const idx = messages.value.indexOf(heldFollowUpMsg)
        if (idx !== -1) {
          messages.value[idx]._holdForReading = false
        } else {
          heldFollowUpMsg._holdForReading = false
        }
        heldFollowUpMsg = null
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('reading-follow-up-shown'))
        }
      }
    }

    if (!ws.value) {
      console.error('WebSocket is not initialized')
      return
    }
    ws.value.onmessage = (event) => {
      const data = JSON.parse(event.data)
      if (!messages || !messages.value) {
        console.error('messages is not defined')
        return
      }
      if (data.context) {
        // The line's first word (or any other line from the Mystic) has
        // landed: the dots go.
        if (groundingArriving.value && SPOKEN_CONTEXTS.has(data.context)
            && String(data.message || '').length) {
          groundingArriving.value = false
        }
        switch (data.context) {
          case 'grounding-arriving': {
            // The Doorman menu header leaves now, as it does when the line lands.
            messages.value = messages.value.filter((m) => m._ref !== 'doorman-intro')
            groundingArriving.value = true
            break
          }
          case 'keeper-greeting': {
            // Dispatch to ReaderPage — ReaderPage handles keeperMessages separately
            window.dispatchEvent(new CustomEvent('keeper-greeting', { detail: data }))
            break
          }
          case 'community-greeting': {
            window.dispatchEvent(new CustomEvent('community-greeting', { detail: data }))
            break
          }
          case 'salutation-message':
          case 'welcome-message':
          case 'reader-conversation':
          case 'welcome-question': {
            if (data.reference === 'reading_is_ready') {
              readingIsReady.value = true
              trackReadingComplete()
            }
            // The post-reading follow-up question (welcome-message +
            // reading_is_ready) is held until the reading illustration is
            // in — unless it's already arrived, in which case show it now.
            const isFollowUp =
              data.context === 'welcome-message' && data.reference === 'reading_is_ready'
            const holdForReading = isFollowUp && !readingImage.value
            const trackHeld = (msg) => {
              if (!holdForReading) return
              heldFollowUpMsg = msg
              if (!followUpRevealTimer) {
                followUpRevealTimer = setTimeout(revealFollowUp, 6000)
              }
            }
            // The grounding greeting is the seeker settling in — a statement.
            // The Doorman menu header ("Welcome. I'm X. What would you like to
            // do?") is a question that belongs above the menu buttons, so it
            // must not still be sitting there when the grounding beat opens.
            if (data.context === 'salutation-message') {
              messages.value = messages.value.filter((m) => m._ref !== 'doorman-intro')
            }
            if (data.context !== currentContext) {
              // The grounding question ('welcome-question') is the seeker's
              // real entry into the session. Once it begins, drop the
              // welcome/salutation preamble ("Welcome. I'm X…", "welcome
              // back…") so the chat leads with the grounding, not a stack
              // of greetings.
              if (data.context === 'welcome-question') {
                // Drop the Doorman intro ("Welcome. I'm X… what would you like
                // to do?") — the menu it introduced is gone. The grounding
                // greeting STAYS: it is the Mystic's first words, and losing
                // them the instant the question lands made the beat read as a
                // loading screen. The question arrives under them.
                messages.value = messages.value.filter((m) => m._ref !== 'doorman-intro')
              }
              currentContext = data.context
              const msg = { text: data.message, type: 'reader', completed: true, _context: data.context, _ref: data.reference, _holdForReading: holdForReading }
              messages.value.push(msg)
              trackHeld(msg)
              if (data.reference === 'talk-close') talkParting.value = data.message
            } else {
              const lastMessage = messages.value[messages.value.length - 1]
              // A frame continues the line of its own stream only: a question
              // asked again (R3.3) after the old one was taken out must not
              // run on from the greeting above it.
              // The gift room's whole lines (the opener, the carried line before
              // her card) are their own bubbles, never the end of the last one.
              if (lastMessage && lastMessage.type === 'reader' && !WHOLE_LINES.has(data.reference)
                  && (!lastMessage._context || lastMessage._context === data.context)) {
                lastMessage.text += data.message
              } else {
                const msg = { text: data.message, type: 'reader', completed: true, _context: data.context, _ref: data.reference, _holdForReading: holdForReading }
                messages.value.push(msg)
                trackHeld(msg)
                if (data.reference === 'talk-close') talkParting.value = data.message
              }
            }
            break
          }
          case 'generic-reading-is-ready': {
            if (data.reference === 'reading_is_ready') {
              readingIsReady.value = true
              trackReadingComplete()
            }
            break
          }
          case 'lock-chat': {
            lock_interface.value = data.message === true || String(data.message).toLowerCase() === 'true'
            break
          }
          case 'reading-session-conversation': {
            let json_message = JSON.parse(data.message)
            messages.value.push({ text: json_message.message, type: json_message.message_type, completed: true })
            break
          }
          case 'select-cards-focus-message': {
            selectCardsFocusMessage.value = data.message
            break
          }
          case 'reading-session-id': {
            let json_message = JSON.parse(data.reference)
            ReadingSessionID.value = data.message
            _state.store.latest_reading_session = ReadingSessionID.value
            _state.store.is_latest_reading_session_shared = json_message.is_shared
            _state.store.latest_reading_session_is_paid = json_message.is_paid
            _state.store.latest_reading_session_type = json_message.reader_session_type
            _state.store.querent_subscribed = json_message.is_reader_subscribed
            // The socket re-announces the session on resume: count each reading once.
            if (ReadingSessionID.value && !isReadingViewed(ReadingSessionID.value)) {
              trackOnce(`reading_start:${ReadingSessionID.value}`, 'reading_start', {
                item_id: ReadingSessionID.value,
                reader: store.current_reader_subdomain,
                reading_type: json_message.reader_session_type,
              })
            }
            break
          }
          case 'resume-selected-cards': {
            // Doorman _resume_delivered_reading hands us the picked
            // cards as JSON so FourCardReading's v-for has rows to
            // render. Mirrors the live-flow population that happens
            // via card_selected() during each pick.
            try {
              const parsed = JSON.parse(data.message)
              if (parsed && Array.isArray(parsed.cards)) {
                load_card_reading_session(parsed.cards)
              }
            } catch (err) {
              console.error('Failed to parse resume-selected-cards:', err)
            }
            break
          }
          case 'talk-state': {
            // Talk it through: the server's word on the conversation after
            // the reading (turns left, a card on offer, the paid continue).
            try {
              _state.store.talk = JSON.parse(data.message)
            } catch (err) {
              console.error('Failed to parse talk-state:', err)
            }
            break
          }
          case 'resume-card-selection': {
            // Continue a mid-draw reading: re-arm the picker seeded with the
            // cards already pulled (ReaderPage watches resumeCardSelection).
            try {
              resumeCardSelection.value = JSON.parse(data.message)
            } catch (err) {
              console.error('Failed to parse resume-card-selection:', err)
            }
            break
          }
          case 'instructions': {
            ChatInstructions.value = data.message
            break
          }
          case 'loading-headsup': {
            loadingHeadsUpMessage.value = data.message
            break
          }
          case 'widget': {
            const widgetTypeMap = { calendar: 'birthdate', birthplace_map: 'birthplace_map', collect_email_form: 'collect_email_form' }
            const widgetType = widgetTypeMap[data.reference]
            if (widgetType) {
              setWidget(widgetType, data.message)
            } else {
              const pluginWidget = pluginRegistry.widgets[data.reference]
              if (pluginWidget) {
                setWidget(data.reference, data.message)
              } else {
                console.log('Unhandled widget reference:', data.reference)
              }
            }
            break
          }
          case 'action-button': {
            switch (data.reference) {
              case 'leftAction':
                parseActionButton(data, LeftActionButton, showLeftActionButton, 'LeftActionButton')
                break
              case 'rightAction':
                parseActionButton(data, RightActionButton, showRightActionButton, 'RightActionButton')
                break
              case 'in-chat-window':
                try {
                  const incoming = JSON.parse(data.message)
                  // The Altar's menu arriving late never replaces a reading's
                  // bar (utils/reading-bar.js, reading 4729, 2026-10-21).
                  if (keepReadingBar({ current: StaticSuggestions.value, incoming,
                                       readingOnScreen: readingIsReady.value,
                                       conversationState: lastState && lastState.store ? lastState.store.conversation_state : '' })) break
                  StaticSuggestions.value = incoming
                  disable_insight_help.value = false
                } catch (error) {
                  console.error('Failed to parse StaticSuggestions:', error, data.message)
                  StaticSuggestions.value = []
                }
                break
              default:
                console.log('Unhandled action-button reference:', data.reference)
            }
            break
          }
          case 'tarot-session-detail': {
            // Per-position descriptions (readingPast / readingFocus /
            // future spreads' readingCrown / readingFoundation / etc.)
            // ALL accumulate into the readingPositions map. The legacy
            // per-role refs (readingPast, readingPresent, readingFuture,
            // readingAdvice) are mirrored from the same handler so
            // FourCardReading.vue can keep reading them directly.
            // Non-position-shaped refs (titles, analysis, image) keep
            // their dedicated refs — those are global to the reading,
            // not per-position.
            //
            // This distinction used to be drawn here and ignored a few lines
            // above, where the held closing card collected `detail` by
            // appending EVERY frame that arrived. The card is held out of the
            // chat until the summary lands, so it stays last in the array and
            // swallowed the lot: the weave, the summary title, the shared
            // title, the illustration title, and the raw blob URL of the
            // image, concatenated without spaces into the advice bubble. That
            // shipped. The closing beat is a card and a line of counsel — the
            // fuller read is what the weave above it is for — so there is no
            // `detail` to build any more.
            const positionLikeRefs = new Set([
              'readingPast',
              'readingPresent',
              'readingFuture',
              'readingAdvice',
            ])
            const ref = data.reference || ''
            const isLegacyPositionRef = positionLikeRefs.has(ref)
            const isNewPositionRef =
              ref !== '' &&
              ref.startsWith('reading') &&
              !isLegacyPositionRef &&
              ![
                'readingTitle',
                'readingIllustrationTitle',
                'readingAnalysis',
                'readingImage',
                'ReadingSummaryTitle',
              ].includes(ref)

            if (isLegacyPositionRef || isNewPositionRef) {
              const prev = readingPositions.value[ref] || ''
              readingPositions.value[ref] = prev + data.message
            }

            switch (ref) {
              case 'readingTitle':
                readingTitle.value += sanitizeString(data.message)
                break
              case 'readingPast':
                readingPast.value += data.message
                break
              case 'readingPresent':
                readingPresent.value += data.message
                break
              case 'readingFuture':
                readingFuture.value += data.message
                break
              case 'readingAdvice':
                readingAdvice.value += data.message
                break
              case 'readingIllustrationTitle':
                readingIllustrationTitle.value += sanitizeString(data.message)
                break
              case 'readingAnalysis':
                readingAnalysis.value += data.message
                break
              case 'ReadingSummaryTitle':
                ReadingSummaryTitle.value += sanitizeString(data.message)
                break
              case 'readingImage':
                readingImage.value = data.message
                // Reading fully loaded — surface any held follow-up question.
                revealFollowUp()
                break
              default:
                if (!isNewPositionRef) {
                  console.log('Unhandled reference:', data.reference)
                }
            }
            break
          }
          case 'astrology-session-detail': {
            switch (data.reference) {
              case 'teaser':
                astrologyTeaser.value += data.message
                break
              case 'summary':
                astrologySummary.value += data.message
                break
              case 'summary_title':
                astrologySummaryTitle.value += data.message
                break
              case 'shared_title':
                astrologySharedTitle.value += data.message
                break
              case 'image_caption':
                astrologyImageCaption.value += data.message
                break
              case 'image':
                astrologyImage.value = data.message
                break
              case '2_hour_limit':
                astrologyTwoHourLimit.value = data.message
                currentWidget.value = {
                  type: 'countdown_timer',
                  payload: JSON.parse(data.message),
                }
                break
              case 'await_email_activation_window':
                awaitEmailActivationWindow.value = true
                break
              case 'disable_page_after_email_activation':
                disablePageAfterEmailActivation.value = true
                break
              default:
                console.log('Unhandled reference:', data.reference)
            }
            break
          }
          case 'shared-tarot-session-detail': {
            switch (data.reference) {
              case 'sharedReadingTitle':
                sharedReadingTitle.value = sanitizeString(data.message)
                break
              case 'readingPast':
                sharedReadingPast.value = data.message
                break
              case 'readingPresent':
                sharedReadingPresent.value = data.message
                break
              case 'readingFuture':
                sharedReadingFuture.value = data.message
                break
              case 'readingAdvice':
                sharedReadingAdvice.value = data.message
                break
              case 'sharedIllustrationTitle':
                sharedIllustrationTitle.value = sanitizeString(data.message)
                break
              case 'sharedReadingAnalysis':
                sharedReadingAnalysis.value = data.message
                break
              case 'sharedReadingSummaryTitle':
                sharedReadingSummaryTitle.value = sanitizeString(data.message)
                break
              case 'sharedReadingImage':
                sharedReadingImage.value = data.message
                break
              default:
                console.log('Unhandled reference:', data.reference)
            }
            break
          }
          case 'tarot-session-image': {
            const card_info = JSON.parse(data.reference)
            let reversed_card = card_info.card_is_reversed === 'true'
            messages.value.push({
              imageUrl: data.message,
              text: '',
              type: 'user',
              reversed: reversed_card,
              card: card_info.message,
              // Tagged by the BE when this position reveals after_summary.
              _holdForSummary: card_info.hold_until_summary === true,
              // Live: the headline is on its way. The panel shows its quiet
              // "reading" line until the words come (2026-10-23), and never
              // for longer than the deck's hard max.
              _awaitingWords: true,
            })
            const panel = messages.value[messages.value.length - 1]
            setTimeout(() => { if (panel && panel._awaitingWords) panel._awaitingWords = false }, PACE.maxMs)
            break
          }
          // A pulled card (Talk it through, or her card in a gift room;
          // 2026-10-22 API §6): ONE panel in the chat. `talk-card` at the pick
          // (live: no words yet; replay: filled), `talk-card-read` fills it.
          case 'talk-card': {
            let card = null
            try { card = JSON.parse(data.message) } catch (err) { console.error('Failed to parse talk-card:', err) }
            if (!card) break
            const id = String(card.id || data.reference || '')
            const at = messages.value.findIndex((m) => m && m._context === 'talk-card' && m.talk_card && m.talk_card.id === id)
            const filled = !!(card.reading || card.line)
            const tc = { id, kind: card.kind || 'talk', card: card.card || '', image: card.image || '',
              reversed: card.reversed === true || card.reversed === 'true', question: card.question || '',
              line: card.line || '', reading: card.reading || '' }
            // The card drawn from a lesson (lesson-redo E2.5): "Your card".
            // A reload of the draw (API.md 6.2 v1.4) replays it without the flag.
            if (isLessonCard(card, { filled, live: store.lesson_draw_live, resume: store.lesson_draw_resume })) {
              tc.lesson_draw = true; store.lesson_draw_live = false; store.lesson_draw_resume = false
            }
            if (at !== -1) {
              messages.value[at].talk_card = { ...messages.value[at].talk_card, ...tc }
            } else {
              messages.value.push({ text: '', type: 'reader', completed: true, _context: 'talk-card', talk_card: tc,
                // Live (the pick just landed): the card turns over, the pane folds.
                _live: !filled })
            }
            break
          }
          case 'talk-card-read': {
            let read = null
            try { read = JSON.parse(data.message) } catch (err) { console.error('Failed to parse talk-card-read:', err) }
            if (!read) break
            const id = String(read.id || data.reference || '')
            const at = messages.value.findIndex((m) => m && m._context === 'talk-card' && m.talk_card && m.talk_card.id === id)
            if (at !== -1) {
              messages.value[at].talk_card = { ...messages.value[at].talk_card,
                line: read.line || '', reading: read.reading || '', read: true }
            }
            break
          }
          case 'card-selected-in-chat-window': {
            const lastMessage = messages.value[messages.value.length - 1]
            if (data.is_new_message) {
              if (
                lastMessage &&
                lastMessage.type === 'user' &&
                lastMessage.imageUrl &&
                lastMessage.text === ''
              ) {
                lastMessage.text += data.message
                lastMessage._awaitingWords = false
              } else {
                messages.value.push({
                  text: data.message,
                  type: 'user',
                  is_card: true,
                })
              }
            } else {
              lastMessage.text += data.message
            }
            break
          }
          case 'action': {
            console.log('Action Coming through')
            console.log(data.message)
            break
          }
          default: {
            const contextHandlers = pluginRegistry.wsHandlers[data.context]
            if (contextHandlers) {
              const refKey = data.reference || '_default'
              const handler = contextHandlers[refKey] || contextHandlers['_default']
              if (handler) {
                try {
                  handler.handler(data, messages)
                } catch (err) {
                  console.error(`[Plugin WS] Error in handler for "${data.context}":`, err)
                }
              } else {
                console.log('Unknown channel message:', data)
              }
            } else {
              console.log('Unknown channel message:', data)
            }
          }
        }
      } else {
        console.log('NO CONTEXT:' + data.context)
      }
    }
  }

  // ─── API calls ────────────────────────────────────────────────── //
  function getWelcomeMessage(session_id, token) {
    const urlParams = new URLSearchParams(window.location.search)
    const ref = urlParams.get('ref')

    const postData = {
      session_id: session_id,
      token: token,
      querent_id: localStorage.getItem('qid'),
      ref: ref,
      // Authoritative first-vs-return flag (from start-session) — the BE gates
      // the Doorman welcome greeting on it (greet only on a first meeting).
      is_first_visit: _state.store.is_first_visit,
    }
    let welcome_url = `${_state.static_store.apiUrl}/reader/welcome/`

    return axios
      .post(welcome_url, postData)
      .then((response) => {
        _state.store.conversation_state = response.data.reader_state
        // 2026-05-21 — propagate the BE's lock_chat signal so the
        // chat input (Respond textarea) hides when the seeker lands
        // on a menu/anteroom state like DOORMAN. Doorman.lock_chat
        // is True; conversational states (TarotFunnel, Education,
        // etc.) return False and the input stays available.
        //
        // Lock ONLY when the BE explicitly says so; default to UNLOCKED.
        // (Was: assign only when lock_chat is a boolean — so an ABSENT
        // lock_chat left a stale lock carried from a prior DOORMAN visit,
        // hiding the Respond input and stranding the seeker with no way to
        // talk to the Mystic on a conversational welcome like TarotFunnel.)
        // Matches the conversation-endpoint path below, which already
        // assigns unconditionally.
        lock_interface.value = response.data.lock_chat === true
        lessonWelcome.value = lessonWelcomeOf(response.data)
        return response.data.quick_actions
      })
      .catch((error) => {
        console.error('Error in getWelcomeMessage:', error)
      })
      .finally(() => {
        _state.store.is_streaming_chat = false
      })
  }

  // ``extra``: more fields for the turn, e.g. ``hold_opening_question`` from
  // "A reading for someone" (R3.3: the first question waits for the name).
  const ReaderConversation = async (QuerentState, TheMessage, action_type, conversation_type, extra = null) => {
    const postData = {
      message: TheMessage,
      session_id: QuerentState.store.session_id,
      action_type: action_type,
      conversation_type: conversation_type,
      ...(extra || {}),
    }
    let conversation_url = `${QuerentState.static_store.apiUrl}/reader/conversation/`
    let og_conversation_state = QuerentState.store.conversation_state
    const sentSession = QuerentState.store.session_id

    try {
      QuerentState.store.is_streaming_chat = true
      const response = await axios.post(conversation_url, postData)
      // A reply for a reading the session has left (API.md 6.6): dropped.
      if (isLateReply(response.data, { sentSession, session: QuerentState.store.session_id })) return undefined
      QuerentState.store.conversation_state = response.data.conversation_state
      lock_interface.value = response.data.lock_chat
      // Doorman v1 — surface the BE's readiness signal so the FE
      // can visually emphasize the Begin Reading button when the
      // Interview state's refine_instructions step judges the
      // seeker ready. Always coerce to bool — defaults to false
      // when the BE payload predates Phase 2 or the state has no
      // determine() step.
      QuerentState.store.ready_for_reading = Boolean(
        response.data.ready_for_reading,
      )
      if (response.data.lesson_full) {
        lessonFullReply.value = { lesson_full: response.data.lesson_full, quick_actions: response.data.quick_actions || [] }
      }
      if (response.data.reading_charge) {
        readingChargeReply.value = { reading_charge: response.data.reading_charge, quick_actions: response.data.quick_actions || [] }
      }

      return response.data.quick_actions
    } catch (error) {
      QuerentState.store.is_streaming_chat = false
      console.error(error)
    } finally {
      QuerentState.store.is_streaming_chat = false
    }
    if (og_conversation_state !== QuerentState.store.conversation_state) {
      console.log('We have a state change!')
    }
  }

  // `charge`: a lesson's draw is paid at the pick (API.md 6.3), but the answer
  // waits for the card's reading (38-75s): the balance steps down at once and
  // takes the server's figure when it comes, or goes back (lesson walk B10).
  function communicateCardSelected(state, the_card, how_many_cards_selected, { charge = 0 } = {}) {
    const before = state.store.querent_carat_balance
    const optimistic = Number(charge) > 0 && typeof before === 'number' && state.store.querent_carat_balance_loaded !== false
    if (optimistic) state.store.querent_carat_balance = Math.max(0, before - Number(charge))
    const restore = () => { if (optimistic) state.store.querent_carat_balance = before }
    const sentSession = state.store.session_id
    const postData = {
      session_id: state.store.session_id,
      card_id: the_card.card_id,
      reversed: the_card.reversed,
      selection_order: how_many_cards_selected,
    }
    const csrfToken = localStorage.getItem('csrfToken')
    let card_selected_url = `${state.static_store.apiUrl}/reading/card-selected/`
    return axios
      .post(card_selected_url, postData, {
        headers: {
          'X-CSRFToken': csrfToken,
        },
      })
      .then((response) => {
        // Read for a reading the session has left meanwhile (API.md 6.6): the
        // screen stays as it is, the balance too (the next reading may have
        // taken its own since).
        if (isLateReply(response.data, { sentSession, session: state.store.session_id })) return { movedOn: true }
        // card-selected answers with quick_actions only; writing its missing
        // reader_state here set the store to undefined, which the state
        // watcher took for "left the reading" and tore the pane down.
        if (response.data.reader_state) {
          state.store.conversation_state = response.data.reader_state
        }
        // A lesson's draw is charged at the pick (lesson-redo API.md 6.3): the balance steps down.
        if (typeof response.data.carat_balance === 'number') state.store.querent_carat_balance = response.data.carat_balance
        // No new figure (a retry, "already_selected"): nothing was taken now.
        else restore()
        return response.data.quick_actions
      })
      .catch((error) => {
        // 402: not enough carats for the draw; nothing was drawn (API.md 6.3).
        const d = error && error.response && error.response.data
        if (error && error.response && error.response.status === 402) {
          if (d && typeof d.carat_balance === 'number') state.store.querent_carat_balance = d.carat_balance
          else restore()
          return { failed: true, insufficient: true, error }
        }
        // The pick never landed (server reloading, network). Tell the caller
        // so it can uncount the card: a swallowed failure left the FE one
        // card ahead of the BE and the reading crashed on the last pick.
        console.error('card-selected did not reach the server:', error)
        restore()
        return { failed: true, error }
      })
      .finally(() => {
        state.store.is_streaming_chat = false
      })
  }

  // ─── Pure utility functions ───────────────────────────────────── //
  function getSunSign(month, day) {
    const signs = [
      ['Capricorn', 1, 19], ['Aquarius', 2, 18], ['Pisces', 3, 20],
      ['Aries', 4, 19], ['Taurus', 5, 20], ['Gemini', 6, 20],
      ['Cancer', 7, 22], ['Leo', 8, 22], ['Virgo', 9, 22],
      ['Libra', 10, 22], ['Scorpio', 11, 21], ['Sagittarius', 12, 21],
      ['Capricorn', 12, 31],
    ]
    for (let i = 0; i < signs.length - 1; i++) {
      const [sign, endMonth, endDay] = signs[i]
      if ((month === endMonth && day <= endDay) || month < endMonth) {
        return sign
      }
    }
    return 'Capricorn'
  }

  function formatPrettyDate(birthdateStr) {
    const [year, month, day] = birthdateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return date.toLocaleDateString(undefined, options)
  }

  // ─── Return all public state and functions ────────────────────── //
  return {
    // WebSocket ref + lifecycle helpers
    ws,
    ensureWebSocketConnected,

    // UI state refs
    lock_interface,
    disable_insight_help,
    ReadingSessionID,
    loadingHeadsUpMessage,
    groundingArriving,

    // Reading state refs
    readingTitle,
    readingPast,
    readingPresent,
    readingFuture,
    readingAdvice,
    readingPositions,
    readingAnalysis,
    readingIllustrationTitle,
    readingImage,
    ReadingSummaryTitle,
    readingIsReady,

    // Astrology state refs
    astrologyTeaser,
    astrologySummary,
    astrologySummaryTitle,
    astrologySharedTitle,
    astrologyImageCaption,
    astrologyImage,
    astrologyTwoHourLimit,

    // Shared reading refs
    sharedReadingTitle,
    sharedReadingImage,
    sharedReadingPast,
    sharedReadingPresent,
    sharedReadingFuture,
    sharedReadingAdvice,
    sharedReadingSummaryTitle,
    sharedReadingAnalysis,
    sharedIllustrationTitle,

    // Chat UI refs
    ChatInstructions,
    LeftActionButton,
    RightActionButton,
    StaticSuggestions,
    selecting_cards,
    resumeCardSelection,
    lessonSlotLabelsLive,
    lessonFullReply,
    readingChargeReply,
    lessonWelcome,
    talkParting,
    showLeftActionButton,
    showRightActionButton,

    // Other UI refs
    scrollToShareTrigger,
    selectCardsFocusMessage,
    currentWidget,
    disablePageAfterEmailActivation,
    awaitEmailActivationWindow,

    // Functions
    resetAstrologyReadingState,
    resetReadingState,
    connectWebSocket,
    disconnectWebSocket,
    handleWebSocketMessages,
    revealSummaryHeldCards,
    getWelcomeMessage,
    ReaderConversation,
    communicateCardSelected,
    getSunSign,
    formatPrettyDate,
  }
}
